import { useEffect, useState } from "react";
import qapi from "views_shared/app/javascript/api";
import qlog from "views_shared/app/javascript/scripts/qlog";

/**
 * useStoryIdeas — data half of the Expert/Hybrid profile's "Story Ideas" section (the chrome half
 * is story_ideas_card). Reads the enrich-scored `public_pitches` examples that named this expert
 * PromoEntity as a pitched target — AI-scored real pitches, keyed via `pitched_promo_entity_ids`
 * (see `qapi.publicPitches.getAllForExpert`).
 *
 * Idles (settling empty, so the section can collapse) until it has an `expertId`, mirroring
 * useTeamMembers/useMediaOutletsRail's shape. Never throws: this is display content, not a form,
 * so a failed read settles empty rather than surfacing to the page — both failure shapes
 * (`getResource` resolving `{ error }` on a non-2xx, and a rejected transport promise) log and
 * settle empty.
 *
 * @param {Object} options
 * @param {number|string} [options.expertId] - The expert PromoEntity id to load pitches for
 * @returns {{ pitches: Array, isLoading: boolean }} `pitches` are the raw `public_pitches` view
 *   rows (`title`, `lede`, `text`, ...), empty while idle, loading, or after a failed read
 */
function useStoryIdeas({ expertId }) {
  const [pitches, setPitches] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // No expert id yet (profile still loading): settle empty rather than leaving the card in a
    // skeleton that never resolves.
    if (!expertId) {
      setPitches([]);
      setIsLoading(false);
      return undefined;
    }

    let componentIsMounted = true;

    const load = async () => {
      try {
        setIsLoading(true);

        const response = await qapi.publicPitches.getAllForExpert(expertId);
        if (!componentIsMounted) return;

        // getResource RESOLVES `{ error }` on a non-2xx instead of throwing, so THIS branch — not
        // the catch below — is the one an ordinary PostgREST failure takes.
        if (response?.error) {
          qlog.error("Story Ideas lookup failed:", response.error);
          setPitches([]);
          return;
        }

        setPitches(Array.isArray(response?.data) ? response.data : []);
      } catch (error) {
        // The transport itself failed (network down, token refresh threw) — a rejection, not `{ error }`.
        qlog.error("Story Ideas lookup failed:", error);
        if (componentIsMounted) setPitches([]);
      } finally {
        if (componentIsMounted) setIsLoading(false);
      }
    };
    load();

    return () => {
      componentIsMounted = false;
    };
  }, [expertId]);

  return { pitches, isLoading };
}

export default useStoryIdeas;
